import { cacher } from '@/utils/rtkQueryCacheUtils'
import { baseApi, Tag } from './base' // Assuming your baseApi is exported from here
import { consultationApi } from './consultation'

import type { DisplayMessage as CoreDisplayMessage, ClientSessionMetadata } from '@/core/models/medconnect-agent.model' // Use an alias if needed locally and added ClientSessionMetadata
import type { Biological } from '@/core/models/consultation.model'

// Response type from GET /models/available
export interface DiscoveredModel {
  id: string
  name: string
  provider: string
}

export interface AvailableModelsResponse {
  activeProvider: string
  providers: Array<{
    id: string
    catalogModels: Record<string, string>
    discoveredModels?: DiscoveredModel[]
  }>
}

// Backend response type from POST /chat
interface ChatApiResponse {
  output: string // AI response text
  sessionId: string
  messageId: string // ID of the user's message for file attachments
  toolLogs?: any[] // Add optional tool logs
  // Use a more specific type for chatHistory items if possible, from backend schema or a mapped frontend type
  // For now, it's any[], but could be CoreDisplayMessage[] if mapped before use, or a backend message type array.
  chatHistory?: any[] // This is the history after a POST /chat call
  // Added accessToken and stream as they are used in ChatInterface, but API doesn\'t show them.
  // Backend query for POST /chat only has sessionId and messageContent
  // Let\'s assume accessToken is handled by baseApi and stream isn\'t a query param for this endpoint.
  // If attachedFiles are part of the body, it needs to be added here. The query in ChatInterface includes it.
  // For now, sticking to what the query definition has.
  // If `attachedFiles` should be part of `SendChatArg`, this needs to be reflected in the `query` function below.
  // The mutation call in ChatInterface passes `attachedFiles`. The query definition in medconnectAgentApi
  // *only* passes `sessionId` and `messageContent` in the body. This is a mismatch.
  // Let's assume the backend endpoint /medconnect-agent/chat is updated to receive `attachedFiles` and `stream` options.
  // Or, those are handled differently (e.g. stream via a different endpoint/header, files via pre-signed URLs then IDs in message)
  // Given the existing structure in ChatInterface, let's add `attachedFiles` to SendChatArg and the body.
  // `stream` is not part of the body in the existing `sendMessage` in ChatInterface; it's a parameter to the hook,
  // which usually controls how RTK Query handles the response (e.g., for streaming updates, which isn't implemented here yet).
  // For `attachedFiles`, if they are sent in the body:
  attachedFiles?: Array<{ key: string; filename: string; objectType?: string }>
}

// Argument for the sendMessage mutation
interface SendChatArg {
  sessionId?: string // Optional for first message
  messageContent: string
  timezone?: string
  attachedFiles?: Array<{ key: string; filename: string; objectType?: string }>
  agentType?: string
  language?: string
}
// interface ChatHistoryResponse { // This will be removed
// history: CoreDisplayMessage[];
// }

// Get base URL once
export const medconnectAgentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Remove the streamAgent endpoint
    // streamAgent: builder.query<...>(...),

    // Updated endpoint to send messages and receive response
    sendMessage: builder.mutation<ChatApiResponse, SendChatArg>({
      query: ({ sessionId, messageContent, attachedFiles, timezone, agentType, language }) => ({
        // Added attachedFiles
        url: `/medconnect-agent/chat`,
        method: 'POST',
        body: { sessionId, messageContent, attachedFiles, timezone, agentType, language }, // Added attachedFiles
      }),
      // invalidatesTags: (result, error, { sessionId }) => sessionId ? [] : [{ type: 'ChatSessions', id: 'LIST' }],
      // Updated invalidation logic:
      // If a message is sent to an existing session, invalidate that session's history.
      // If it's a new session (no sessionId passed or backend confirms new), invalidate the list of sessions.
      invalidatesTags: (result, error, arg) => {
        const tags: Array<{ type: Tag; id?: string | number }> = []
        if (result && result.sessionId) {
          tags.push({ type: Tag.AGENT_CHAT_HISTORY, id: result.sessionId }) // Use as const
          // If the message initiated a brand new session from the client's perspective (arg.sessionId was undefined)
          // then also invalidate the list of sessions.
          if (!arg.sessionId) {
            tags.push({ type: Tag.AGENT_CHAT_SESSIONS, id: 'LIST' }) // Use as const
          }
        } else if (!arg.sessionId) {
          // If it was intended as a new session and somehow failed before getting a sessionId in result, still try to invalidate list.
          tags.push({ type: Tag.AGENT_CHAT_SESSIONS, id: 'LIST' }) // Use as const
        }
        return tags
      },
    }),

    // New endpoint to get chat history for a specific session
    getChatHistory: builder.query<{ history: CoreDisplayMessage[]; metadata?: ClientSessionMetadata }, { sessionId: string; agentType?: string }>({
      query: ({ sessionId, agentType }) => {
        const params = agentType ? `?agentType=${encodeURIComponent(agentType)}` : ''
        return `/medconnect-agent/chat/history/${sessionId}${params}`
      },
      providesTags: (result, error, arg) => cacher.cacheByIdArg(Tag.AGENT_CHAT_HISTORY)(result, error, arg.sessionId),
    }),

    // New endpoint to get all chat sessions for the current user
    getAllSessions: builder.query<ClientSessionMetadata[], string | undefined>({
      query: (agentType) => {
        const params = agentType ? `?agentType=${encodeURIComponent(agentType)}` : ''
        return `/medconnect-agent/chat/sessions${params}`
      },
      providesTags: (result) =>
        result
          ? [...result.map(({ sessionId }) => ({ type: Tag.AGENT_CHAT_SESSIONS, id: sessionId })), { type: Tag.AGENT_CHAT_SESSIONS, id: 'LIST' }]
          : [{ type: Tag.AGENT_CHAT_SESSIONS, id: 'LIST' }],
    }),
    generateExpertiseMessage: builder.mutation<
      { success: boolean; message?: string; error?: string },
      { expertiseId: string; context?: string; includeHistory?: boolean; language?: string }
    >({
      query: (body) => ({
        url: '/medconnect-agent/generate-expertise-message',
        method: 'POST',
        body,
      }),
    }),

    // OCR and add to consultation endpoint
    ocrAndAddToConsultation: builder.mutation<
      {
        success: boolean
        biologicalTestsAdded?: number
        biologicalTestsUpdated?: number
        biologicalTests?: Biological[]
        vitalSignsUpdated?: number
        error?: string
      },
      { fileKey: string; consultationId: string }
    >({
      query: (body) => ({
        url: '/medconnect-agent/ocr-and-add',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { consultationId }) => {
        if (result?.success) {
          return [{ type: Tag.CONSULTATION, id: consultationId }, Tag.CONSULTATION]
        }
        return []
      },
      async onQueryStarted({ consultationId }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        // If successful, refetch the consultation to sync RTK Query cache and Redux store
        if (data?.success) {
          // This will trigger a refetch which will update Redux via extraReducers
          dispatch(consultationApi.endpoints.getConsultation.initiate(consultationId, { forceRefetch: true }))
        }
      },
    }),

    // Get available LLM models/providers for the model selector
    getAvailableModels: builder.query<AvailableModelsResponse, { discover?: boolean } | void>({
      query: (args) => {
        const discover = args && 'discover' in args ? args.discover : false
        return `/medconnect-agent/models/available${discover ? '?discover=true' : ''}`
      },
    }),

    // Extract vital signs from document endpoint
    extractVitalSigns: builder.mutation<
      { success: boolean; message: string; vitalSignsUpdated?: number; error?: string },
      { fileKey: string; consultationId: string; sessionId?: string; timezone?: string }
    >({
      query: (body) => ({
        url: '/medconnect-agent/extract-vital-signs',
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
})
